import { Table, TableBody, TableContainer } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import {
  localeStringOptions,
  MESSAGE_TYPE,
} from "../../../common/constants/common.constants";
import { IWithdrawalDataDashboard } from "../../../common/interfaces/user-dashboard.interface";
import {
  calculateWithdrawnAmount,
  getWithdrawalProposalsInfo,
  getWithdrawalsData,
  parseUnixToDate,
} from "../../../solana/common/helpers/user.dashboard.helpers";
import { createNotification } from "../../../utilities/notification";
import ClubTableCell from "../ClubTableCell/ClubTableCell";
import DashboardTableCell from "../DashboardTable/DashboardTableCell/DashboardTableCell";
import TableHeading from "../DashboardTable/TableHeading/TableHeading";
import TableInfo from "../TableInfo/TableInfo";
import TableTitleAndFilters from "../TableTitleAndFilters/TableTitleAndFilters";
import arrows from "../../../assets/arrows.svg";
import "./WithdrawalsTable.scss";
import DashboardClaimButton from "../DashboardClaimButton/DashboardClaimButton";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import useModifier from "../../../hooks/useModifier";
import { CLUB_DETAILS } from "../../../common/constants/routes.constants";
import { PublicKey } from "@solana/web3.js";
import DashboardTableRow from "../DashboardTable/DashboardTableRow/DashboardTableRow";
import { WITHDRAWAL_TABLE_COLUMNS } from "../../../common/constants/dashboard.constants";
import { tableWrapper } from "../DashboardTable/TableHeading/TableHeading.styles";
import { DashboardFilter } from "../../../common/enums/dashboard.enums";
import { dashboardStore } from "../../../contexts/dashboardStore";

const WithdrawalsTable = () => {
  const { clubs, setWithdrawals, withdrawals, treasuryData, deposits } =
    dashboardStore();
  const wallet = useAnchorWallet();

  useEffect(() => {
    if (!withdrawals) void getWithdrawals();
    else {
      void getWithdrawalProposals(withdrawals);
    }
  }, []);

  const { filteredItems, addFilterByName, addFilterItem } = useModifier(
    withdrawals ?? [],
    DashboardFilter.WithdrawalClaimed
  );

  const getWithdrawals = async () => {
    try {
      const loadedWithdrawals = await getWithdrawalsData(
        clubs!,
        treasuryData!,
        wallet!,
        deposits!
      );
      setWithdrawals(loadedWithdrawals);
      getWithdrawalProposals(loadedWithdrawals);
    } catch (error) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem loading withdrawal data!"
      );
    }
  };

  const getWithdrawalProposals = async (
    withdrawals: IWithdrawalDataDashboard[]
  ) => {
    try {
      if (withdrawals && withdrawals.length > 0) {
        const proposalAddresses: PublicKey[] = [];
        const withdrawalsToFetch: IWithdrawalDataDashboard[] = [];
        withdrawals!.forEach((withdrawal) => {
          if (!withdrawal.proposalAccount) {
            withdrawalsToFetch.push(withdrawal);
            proposalAddresses.push(withdrawal.relatedProposal);
          }
        });
        let updatedWithdrawals = [...withdrawals];
        if (proposalAddresses.length > 0) {
          const proposals = await getWithdrawalProposalsInfo(proposalAddresses);
          const fetchedWithdrawals = withdrawalsToFetch.map((withdrawal) => {
            return {
              ...withdrawal,
              proposalAccount: proposals.find(
                (p) =>
                  p.pubkey.toString() === withdrawal.relatedProposal.toString()
              ),
            };
          });
          fetchedWithdrawals.forEach((withdrawal) => {
            const withdrawalIndex = updatedWithdrawals.findIndex(
              (acc) =>
                acc.withdrawal.toString() === withdrawal.withdrawal.toString()
            );
            updatedWithdrawals[withdrawalIndex] = {
              ...updatedWithdrawals[withdrawalIndex],
              proposalAccount: withdrawal.proposalAccount,
            };
          });
        }
        setWithdrawals(updatedWithdrawals);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading proposals data!");
    }
  };

  const renderWithdrawals = useMemo(() => {
    if (filteredItems) {
      return filteredItems.map((withdrawalData) => {
        return (
          <DashboardTableRow
            redirectUrl={`${CLUB_DETAILS}/${withdrawalData.club.clubAddress}`}
            key={withdrawalData.withdrawal.toString()}
          >
            <ClubTableCell
              clubImageUrl={withdrawalData.club.clubImageUrl}
              clubName={withdrawalData.club.clubName}
            />
            <DashboardTableCell
              value={withdrawalData.proposalAccount?.account.name}
            />
            <DashboardTableCell
              value={`${withdrawalData.totalWithdraw.toLocaleString(
                undefined,
                localeStringOptions
              )} ${
                withdrawalData.withdrawalMintSymbol &&
                withdrawalData.withdrawalMintSymbol
              }`}
            />
            <DashboardTableCell
              value={withdrawalData.withdrawalRecordAmount.toLocaleString(
                undefined,
                localeStringOptions
              )}
            />
            <DashboardTableCell
              value={
                withdrawalData.proposalAccount &&
                parseUnixToDate(
                  withdrawalData.proposalAccount.account.executingAt!
                ).toString()
              }
            />

            {withdrawalData.withdrawalRecordAmount > 0 ? (
              <DashboardClaimButton
                clubAddress={withdrawalData.club.clubAddress}
                withdrawalAddress={withdrawalData.withdrawal.toString()}
              />
            ) : (
              <DashboardTableCell ignoreSkeleton />
            )}
          </DashboardTableRow>
        );
      });
    }
  }, [filteredItems, withdrawals]);

  return (
    <div className="withdrawals-table">
      <div className="withdrawals-table__info-cards">
        <TableInfo
          icon={arrows}
          title="Active withdrawals"
          description={withdrawals
            ?.filter(
              (withdrawal) =>
                !withdrawal.hasWithdrawn &&
                withdrawal.withdrawalRecordAmount > 0
            )
            .length.toString()}
        />
      </div>
      <TableTitleAndFilters
        filtersData={[
          {
            filterTitle: "Claimed",
            onFilterSelect: () => {
              addFilterItem(true);
            },
          },
        ]}
        tableTitle="List of your clubs' Withdrawals"
      />
      <TableContainer style={tableWrapper}>
        <Table>
          <TableHeading
            columns={WITHDRAWAL_TABLE_COLUMNS}
            showInputField
            onInput={(e) => addFilterByName(e.target.value)}
          />
          <TableBody>{renderWithdrawals}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default WithdrawalsTable;
